import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, Calendar, X, Filter, Search, Loader } from "lucide-react";
import axiosInstance from "../../API/interceptor";

const STATUS_OPTIONS = [
  { value: "open",             label: "Open" },
  { value: "in progress",      label: "In Progress" },
  { value: "on hold",          label: "On Hold" },
  { value: "completed",        label: "Complete" },
  { value: "approval pending", label: "Approval Await" },
  { value: "closed",           label: "Closed" },
  { value: "overdue",          label: "Overdue" },
];

const PRIORITY_OPTIONS = [
  { value: "URGENT", label: "Urgent" },
  { value: "HIGH",   label: "High" },
  { value: "MEDIUM", label: "Medium" },
  { value: "LOW",    label: "Low" },
];


const EMPTY = {
  assigned_to: [], priority: [], status: [], created_by: [],
  informer: [], observer: [], functional_categories: [],
  task_type: "", created_at: "", task_deadline: "", start_date: "",
};

const FL = ({ txt }) => (
  <label className="block text-xs font-bold text-gray-700 mb-1.5">{txt}</label>
);

const DateField = ({ label, value, onChange }) => (
  <div className="mb-4">
    <FL txt={label} />
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded flex items-center justify-center shrink-0 bg-gradient-to-br from-[#7C3AED] to-[#6D28D9]">
        <Calendar size={11} color="#fff" />
      </div>
      <input type="date" value={value} onChange={e => onChange(e.target.value)}
        className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 outline-none bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-50 transition-all"/>
    </div>
  </div>
);

function UserSearchSelect({ label, placeholder, value = [], onChange }) {
  const [open,      setOpen]      = useState(false);
  const [search,    setSearch]    = useState("");
  const [results,   setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const abortRef    = useRef(null);
  const inputRef    = useRef(null);

  const doSearch = useCallback((term) => {
    clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    debounceRef.current = setTimeout(async () => {
      abortRef.current = new AbortController();
      setSearching(true);
      try {
        const res = await axiosInstance.get("/task_assignment/get_users_all", {
          params: { q: term || "" },
          signal: abortRef.current.signal,
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setResults(data.map(u => ({
          value: String(u.staffid),
          label: u.display_name || u.alias_name || u.email || String(u.staffid),
        })));
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        setResults([]);
      } finally { setSearching(false); }
    }, 300);
  }, []);

  const handleOpen = () => { setOpen(true); setSearch(""); doSearch(""); setTimeout(() => inputRef.current?.focus(), 50); };
  const toggle = (val, lbl) => {
    if (value.find(v => v.value === val)) onChange(value.filter(v => v.value !== val));
    else onChange([...value, { value: val, label: lbl }]);
  };
  const isSelected = (val) => value.some(v => v.value === val);

  return (
    <div className="mb-4 relative">
      <FL txt={label} />
      <div onClick={handleOpen}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer flex items-center justify-between transition-all hover:border-violet-300 min-h-[38px]">
        <span className={value.length === 0 ? "text-gray-400" : "text-gray-700"}>
          {value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        <ChevronDown size={13} className={`text-slate-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}/>
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {value.map(v => (
            <span key={v.value} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-50 border border-violet-200 text-violet-700">
              {v.label}
              <button onClick={e => { e.stopPropagation(); onChange(value.filter(x => x.value !== v.value)); }}
                className="border-none bg-transparent cursor-pointer text-violet-400 hover:text-violet-700 leading-none p-0 text-sm">×</button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <>
          <div className="fixed inset-0 z-[1199]" onClick={() => setOpen(false)}/>
          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-[1200] max-h-[260px]">
            <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2">
              {searching
                ? <Loader size={12} className="text-violet-500 animate-spin flex-shrink-0"/>
                : <Search size={12} className="text-slate-400 flex-shrink-0"/>}
              <input ref={inputRef} value={search}
                onChange={e => { setSearch(e.target.value); doSearch(e.target.value); }}
                onClick={e => e.stopPropagation()}
                placeholder="Searching..."
                className="flex-1 border-none outline-none text-xs text-gray-700 bg-transparent"/>
              {search && <button onClick={() => { setSearch(""); doSearch(""); }} className="border-none bg-transparent cursor-pointer text-slate-400 text-sm p-0">×</button>}
            </div>
            <div className="overflow-y-auto max-h-[200px]">
              {searching && results.length === 0
                ? <div className="px-3 py-3 text-xs text-slate-400 text-center">Searching...</div>
                : results.length === 0
                ? <div className="px-3 py-3 text-xs text-slate-400 text-center">No results</div>
                : results.map(o => {
                    const sel = isSelected(o.value);
                    return (
                      <div key={o.value} onClick={e => { e.stopPropagation(); toggle(o.value, o.label); }}
                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-violet-50 transition-colors">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? "border-[#7C3AED] bg-[#7C3AED]" : "border-[#D1D5DB] bg-white"}`}>
                          {sel && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                        <span className="text-xs text-gray-700">{o.label}</span>
                      </div>
                    );
                  })
              }
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MultiSelect({ label, placeholder, options, value = [], onChange, loading = false }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");

  const filtered = options.filter(o => (o.label || "").toLowerCase().includes(search.toLowerCase()));
  const toggle = (val) => {
    if (value.includes(val)) onChange(value.filter(v => v !== val));
    else onChange([...value, val]);
  };

  return (
    <div className="mb-4 relative">
      <FL txt={label} />
      <div onClick={() => { if (!loading) setOpen(p => !p); }}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer flex items-center justify-between transition-all hover:border-violet-300 min-h-[38px]">
        <span className={value.length === 0 ? "text-gray-400" : "text-gray-700"}>
          {loading ? "Loading..." : value.length === 0 ? placeholder : `${value.length} selected`}
        </span>
        <ChevronDown size={13} className={`text-slate-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}/>
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {value.map(v => {
            const opt = options.find(o => o.value === v);
            return (
              <span key={v} className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-50 border border-violet-200 text-violet-700">
                {opt?.label || v}
                <button onClick={e => { e.stopPropagation(); toggle(v); }}
                  className="border-none bg-transparent cursor-pointer text-violet-400 hover:text-violet-700 leading-none p-0 text-sm">×</button>
              </span>
            );
          })}
        </div>
      )}

      {open && !loading && (
        <>
          <div className="fixed inset-0 z-[1199]" onClick={() => setOpen(false)}/>
          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-[1200] max-h-[240px]">
            <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2">
              <Search size={12} className="text-slate-400"/>
              <input autoFocus value={search}
                onChange={e => { e.stopPropagation(); setSearch(e.target.value); }}
                onClick={e => e.stopPropagation()}
                placeholder="Search..."
                className="flex-1 border-none outline-none text-xs text-gray-700 bg-transparent"/>
            </div>
            <div className="overflow-y-auto max-h-[190px]">
              {filtered.length === 0
                ? <div className="px-3 py-3 text-xs text-slate-400 text-center">No results</div>
                : filtered.map(o => {
                    const sel = value.includes(o.value);
                    return (
                      <div key={o.value} onClick={e => { e.stopPropagation(); toggle(o.value); }}
                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-violet-50 transition-colors">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${sel ? "border-[#7C3AED] bg-[#7C3AED]" : "border-[#D1D5DB] bg-white"}`}>
                          {sel && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5L3.5 6L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </div>
                        <span className="text-xs text-gray-700">{o.label}</span>
                      </div>
                    );
                  })
              }
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function FilterPanel({ open, onClose, onApply, clearKey = 0, syncFilters = null }) {
  const [f, setF] = useState({ ...EMPTY, assigned_to: [], created_by: [], informer: [], observer: [] });
  const [funcCatOptions, setFuncCatOptions] = useState([]);
  const [funcCatLoading, setFuncCatLoading] = useState(false);
  const [funcCatLoaded,  setFuncCatLoaded]  = useState(false);

  const clear = () => setF({ ...EMPTY, assigned_to: [], created_by: [], informer: [], observer: [] });

  useEffect(() => { if (clearKey > 0) clear(); }, [clearKey]); // eslint-disable-line

  // Sync internal state when a filter chip is removed from outside (without opening the panel)
  useEffect(() => {
    if (!syncFilters) return;
    setF({
      status:                syncFilters.status                || [],
      priority:              syncFilters.priority              || [],
      assigned_to:           (syncFilters.assigned_to || []).map((v, i) => ({ value: v, label: (syncFilters.assigned_to_names  || [])[i] || v })),
      created_by:            (syncFilters.created_by  || []).map((v, i) => ({ value: v, label: (syncFilters.created_by_names   || [])[i] || v })),
      informer:              (syncFilters.informer    || []).map((v, i) => ({ value: v, label: (syncFilters.informer_names     || [])[i] || v })),
      observer:              (syncFilters.observer    || []).map((v, i) => ({ value: v, label: (syncFilters.observer_names     || [])[i] || v })),
      functional_categories: syncFilters.functional_categories || [],
      task_type:             syncFilters.task_type             || "",
      created_at:            syncFilters.created_at            || "",
      task_deadline:         syncFilters.task_deadline         || "",
      start_date:            syncFilters.start_date            || "",
    });
  }, [syncFilters]); // eslint-disable-line

  useEffect(() => {
    if (!open || funcCatLoaded) return;
    setFuncCatLoading(true);
    axiosInstance.get("/task_assignment/get_functional_categories")
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setFuncCatOptions(data.map(cat => ({ value: cat, label: cat })));
        setFuncCatLoaded(true);
      })
      .catch(() => setFuncCatOptions([]))
      .finally(() => setFuncCatLoading(false));
  }, [open]); // eslint-disable-line

  const setField = k => v => setF(p => ({ ...p, [k]: v }));

  const handleApply = () => {
    onApply({
      status: f.status, priority: f.priority,
      assigned_to: f.assigned_to.map(u => u.value),
      created_by:  f.created_by.map(u => u.value),
      informer:    f.informer.map(u => u.value),
      observer:    f.observer.map(u => u.value),
      assigned_to_names: f.assigned_to.map(u => u.label),
      created_by_names:  f.created_by.map(u => u.label),
      informer_names:    f.informer.map(u => u.label),
      observer_names:    f.observer.map(u => u.label),
      functional_categories: f.functional_categories,
      task_type: f.task_type, created_at: f.created_at,
      task_deadline: f.task_deadline, start_date: f.start_date,
    });
    onClose();
  };

  return (
    <>
      {open && <div onClick={onClose} className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-[1100]"/>}

      <div className={`fixed top-0 right-0 bottom-0 w-[380px] max-w-[95vw] bg-white z-[1101] flex flex-col shadow-[-8px_0_40px_rgba(0,0,0,.13)] transition-transform duration-300 ease-[cubic-bezier(.4,0,.2,1)] ${
        open ? "translate-x-0" : "translate-x-full"
      }`}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#7C3AED] to-[#6D28D9]">
              <Filter size={13} color="#fff"/>
            </div>
            <span className="text-base font-extrabold text-slate-800">Filter</span>
          </div>
          <button onClick={onClose}
            className="w-7 h-7 rounded-lg border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
            <X size={14} className="text-slate-500"/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <UserSearchSelect label="Responsible"  placeholder="Choose User"      value={f.assigned_to}           onChange={setField("assigned_to")}/>
          <MultiSelect      label="Priority"     placeholder="Choose Priority"  options={PRIORITY_OPTIONS}      value={f.priority}              onChange={setField("priority")}/>
          <MultiSelect      label="Status"       placeholder="Choose Status"    options={STATUS_OPTIONS}        value={f.status}                onChange={setField("status")}/>
          <UserSearchSelect label="Accountable"  placeholder="Choose Creator"   value={f.created_by}            onChange={setField("created_by")}/>
          <UserSearchSelect label="Informed"     placeholder="Choose Informer"  value={f.informer}              onChange={setField("informer")}/>
          <UserSearchSelect label="Consulted"    placeholder="Choose Consulted" value={f.observer}              onChange={setField("observer")}/>
          <MultiSelect      label="Functional Categories" placeholder="Choose Categories" options={funcCatOptions} value={f.functional_categories} onChange={setField("functional_categories")} loading={funcCatLoading}/>
          <DateField label="Task Created at" value={f.created_at}    onChange={setField("created_at")}/>
          <DateField label="Task Deadline"   value={f.task_deadline} onChange={setField("task_deadline")}/>
          <DateField label="Start Date"      value={f.start_date}    onChange={setField("start_date")}/>
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-gray-100 bg-slate-50 flex gap-2.5 shrink-0">
          <button onClick={clear}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors bg-white border-[1.5px] border-[#7C3AED] text-[#7C3AED]">
            Clear Filter
          </button>
          <button onClick={handleApply}
            className="flex-1 py-2.5 rounded-xl border-none text-white text-sm font-bold cursor-pointer bg-gradient-to-br from-[#7C3AED] to-[#6D28D9] shadow-[0_3px_12px_rgba(109,40,217,.35)]">
            Filter
          </button>
        </div>
      </div>
    </>
  );
}